import React, { useState, useMemo } from "react";
import { useTheme } from "../../context/ThemeContext";
import { FaSearch } from "react-icons/fa";

// CONSTANTES PARA SCROLLBAR (exportables si se necesitan en otros lugares)
// Ajuste para que 3 items se vean completos y al 4to aparezca el scroll
export const MAX_ITEMS_WITHOUT_SCROLLBAR = 3; 
export const ITEM_HEIGHT = 56; // Cambiado de 40 a 56 para coincidir con min-h-[56px]
export const SCROLLBAR_HEIGHT = "170px"; // (3 items * 56px + un margen)

// Función para altura máxima (reutilizable)  
export const getMaxHeightStyle = (isExpanded, items) => {
  if (!isExpanded || !items || items.length === 0) return "0px";

  if (items.length <= MAX_ITEMS_WITHOUT_SCROLLBAR) {
    return `${items.length * ITEM_HEIGHT}px`;
  }

  return SCROLLBAR_HEIGHT;
};

// Función para obtener clases de scrollbar
export const getScrollbarClasses = (items) => {
 // if (!items || items.length <= MAX_ITEMS_WITHOUT_SCROLLBAR) return "";
  // Para colores dinámicos, usamos clases CSS personalizadas
  return `scrollbar scrollbar-w-[20px] scrollbar-track-gray-100 scrollbar-thumb-rounded-full scrollbar-track-rounded-full`;
};

// Función para obtener overflow
export const getOverflowStyle = items => {
  if (!items || items.length <= MAX_ITEMS_WITHOUT_SCROLLBAR) {
    return { overflowY: "hidden", overflowX: "hidden" };
  }
  return { overflowY: "auto", overflowX: "hidden" };
};

const FilterSection = ({
  title,
  items = [],
  isExpanded,
  toggleExpand,
  checkedValues = [],
  onChange,
  idPrefix,
  disabled = false,
  maxItemsWithoutScrollbar = MAX_ITEMS_WITHOUT_SCROLLBAR,
  scrollbarHeight = SCROLLBAR_HEIGHT,
  itemHeight = ITEM_HEIGHT,

  customAccentColor,

}) => {
  const { getBackground, getBorder, getColor, getTextColor, getMuiTextStyle } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");

  // Filtrar items según término de búsqueda
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [items, searchTerm]);

  // Colores del tema
  const primaryColor = getColor("primary", "#ae1d22");

  const accentColor = customAccentColor || primaryColor;


  const hasScrollbar = filteredItems && filteredItems.length > maxItemsWithoutScrollbar;
  const overflowStyle = getOverflowStyle(filteredItems);

  // Función para altura máxima específica del componente
  const getComponentMaxHeight = () => {
    if (!isExpanded || !filteredItems || filteredItems.length === 0) return "0px";

    if (filteredItems.length <= maxItemsWithoutScrollbar) {
      return `${filteredItems.length * itemHeight}px`;
    }

    return scrollbarHeight;
  };

  // Estilos dinámicos
  const containerStyle = {
    ...getBorder("default"),
    backgroundColor: getBackground("surface", "#ffffff").backgroundColor,
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  };

  const headerStyle = {
    backgroundColor: getBackground("header", "#f8f9fa").backgroundColor,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };

  const titleBorderStyle = {
    borderBottom: `2px solid ${accentColor}`,
  };

  const titleStyle = {
    ...getMuiTextStyle("textPrimary"),
    fontWeight: 500,
    fontSize: "1.125rem",
  };

  const countStyle = {
    backgroundColor: getBackground("badge", "rgba(0,0,0,0.1)").backgroundColor,
    color: getTextColor("textSecondary").color,
    fontSize: "0.875rem",
  };

  const contentStyle = {
    backgroundColor: getBackground("body", "#ffffff").backgroundColor,
    maxHeight: getComponentMaxHeight(),
    ...overflowStyle,
    paddingRight: hasScrollbar ? "4px" : "0",
  };

  const listItemStyle = isLast => ({
    borderBottom: isLast ? "none" : `1px solid ${getColor("divider", "#e0e0e0")}`,
    transition: "background-color 0.2s ease",
  });

  const checkboxStyle = {
    color: accentColor,
    borderColor: getColor("border", "#ced4da"),
    "&:checked": {
      backgroundColor: accentColor,
      borderColor: accentColor,
    },
    "&:focus": {
      borderColor: accentColor,
      boxShadow: `0 0 0 2px ${accentColor}40`,
    },
  };

  const labelStyle = {
    ...getMuiTextStyle("textBody"),
    transition: "color 0.2s ease",
    "&:hover": {
      color: accentColor,
    },
  };

  const emptyMessageStyle = {
    ...getMuiTextStyle("textDisabled"),
    fontStyle: "italic",
  };

  return (
    <div className="mb-4" style={containerStyle}>
      {/* Header del filtro */}
      <div className="p-4 flex justify-between items-center transition-all duration-200 hover:opacity-90" style={headerStyle} onClick={!disabled ? toggleExpand : undefined}>
        <div className="flex-grow pb-2 flex items-center" style={titleBorderStyle}>
          <span style={titleStyle}>{title}</span>
          <span className="ml-2 text-sm px-2 py-1 rounded" style={countStyle}>
            {items?.length || 0}
          </span>
        </div>
        <span className={`ml-2 transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}>
          <svg fill="none" viewBox="0 0 16 16" className="h-8 w-8" style={{ color: accentColor }} xmlns="http://www.w3.org/2000/svg">
            <path d="M8 11L3 6L4 5L8 9L12 5L13 6L8 11Z" fill="currentColor" />
          </svg>
        </span>
      </div>

      {/* Contenido del filtro */}
      <div className="transition-all duration-500 ease-in-out">
        {/* Input de búsqueda */}
        {isExpanded && items?.length > 0 && (
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <input
                type="search"
                placeholder="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 pl-9 text-sm border rounded-md focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: accentColor,
                  backgroundColor: getBackground("input", "#ffffff").backgroundColor,
                  color: getTextColor("textPrimary").color,
                  caretColor: accentColor,
                  ...getMuiTextStyle("textBody"),
                }}
              />
              <FaSearch
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: getColor("textSecondary", "#6c757d") }}
                size={14}
              />
            </div>
          </div>
        )}
        
        <div style={contentStyle}>
          <ul className={hasScrollbar ? "pr-1" : ""}>
            {filteredItems?.map((item, i) => (
              <li
                key={i}
                className="px-4 py-4 flex items-center min-h-[56px] hover:bg-opacity-50 transition-colors"
                style={{
                  ...listItemStyle(i === filteredItems.length - 1),
                backgroundColor: getBackground("listItem", "transparent").backgroundColor,
                ":hover": {
                  backgroundColor: getBackground("hover", "rgba(0,0,0,0.04)").backgroundColor,
                },
              }}>
              <input
                className="h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-offset-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={checkboxStyle}
                checked={Array.isArray(checkedValues) && checkedValues.includes(item.name)}
                onChange={e => onChange(e, item.name)}
                type="checkbox"
                id={`${idPrefix}-${item.name}`}
                disabled={disabled}
              />
              <label className="ml-3 cursor-pointer select-none" style={labelStyle} htmlFor={`${idPrefix}-${item.name}`}>
                {item.name}
              </label>
              </li>
            ))}

            {filteredItems?.length === 0 && searchTerm && (
              <li className="px-4 py-4 italic text-center" style={emptyMessageStyle}>
                No se encontraron resultados para "{searchTerm}"
              </li>
            )}

            {items?.length === 0 && !searchTerm && (
              <li className="px-4 py-4 italic text-center" style={emptyMessageStyle}>
                No hay opciones disponibles
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
